import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Select, InputNumber, Input, Button, Space, message, Alert, Spin } from 'antd';
import { supplierAPI } from '../services/api.ts';
import { SupplierOrder, SupplierOrderDetail, RectificationReason, RectificationPreview, RectificationOption, RectifyDetailPayload } from '../types.ts';
import RectificationPreviewPanel, { optionKey } from './RectificationPreviewPanel.tsx';

const { Option } = Select;
const { TextArea } = Input;

interface SupplierOrderRectifyModalProps {
  visible: boolean;
  order: SupplierOrder | null;
  onCancel: () => void;
  onSuccess: () => void;
}

/**
 * Rettifica Quantità della partita dall'ordine fornitore (utenti con permesso Scrittura su Ordini Fornitori).
 * Porta d'ingresso "ordine" del motore lot_rectification: si sceglie la riga, la
 * nuova quantità e il motivo; l'anteprima mostra dove sta la merce e la fase in
 * cui verrà applicata la differenza (produzione / transito / disponibile), con
 * effetti e blocchi; la conferma applica la cascata su ordine, spedizione e bolletta.
 */
const SupplierOrderRectifyModal: React.FC<SupplierOrderRectifyModalProps> = ({ visible, order, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [reasons, setReasons] = useState<RectificationReason[]>([]);
  const [preview, setPreview] = useState<RectificationPreview | null>(null);
  const [selectedOption, setSelectedOption] = useState<RectificationOption | null>(null);
  const [customsRectified, setCustomsRectified] = useState<boolean | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const details: SupplierOrderDetail[] = order?.order_details || [];

  useEffect(() => {
    if (visible) {
      form.resetFields();
      setPreview(null);
      setSelectedOption(null);
      setCustomsRectified(null);
      supplierAPI.getRectificationReasons().then(setReasons).catch(() => setReasons([]));
    }
  }, [visible, form]);

  const detailLabel = (d: SupplierOrderDetail) =>
    `${d.product?.internal_product_code || `ID ${d.product_id}`} — ${d.quantity} pz` +
    ((d.quantity_cancelled || 0) > 0 ? ` (annullati ${d.quantity_cancelled})` : '') +
    (d.product?.product_description ? ` · ${d.product.product_description}` : '');

  const loadPreview = useCallback(async (option?: RectificationOption | null, rectified?: boolean | null) => {
    if (!order) return;
    const values = form.getFieldsValue();
    if (!values.detail_id || !values.new_quantity) {
      setPreview(null);
      return;
    }
    setLoadingPreview(true);
    try {
      const data = await supplierAPI.previewDetailRectification(order.id, values.detail_id, {
        new_quantity: values.new_quantity,
        stage: option?.stage,
        shipment_id: option?.shipment_id ?? null,
        customs_document_item_id: option?.customs_document_item_id ?? null,
        customs_rectified: rectified ?? null
      });
      setPreview(data);
      if (!option && data.option) setSelectedOption(data.option);
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      message.error(typeof detail === 'string' ? detail : "Errore nel calcolo dell'anteprima");
      setPreview(null);
    } finally {
      setLoadingPreview(false);
    }
  }, [order, form]);

  const handleFieldsChange = () => {
    // ogni modifica di riga/quantità azzera la scelta della fase e ricalcola
    setSelectedOption(null);
    setCustomsRectified(null);
    loadPreview(null, null);
  };

  const handleSelectOption = (opt: RectificationOption) => {
    setSelectedOption(opt);
    setCustomsRectified(null);
    loadPreview(opt, null);
  };

  const handleCustomsRectified = (value: boolean) => {
    setCustomsRectified(value);
    loadPreview(selectedOption, value);
  };

  const needsCustomsChoice = !!selectedOption && selectedOption.stage === 'available' && !!selectedOption.customs_document_item_id;
  const canConfirm = !!preview && preview.blocks.length === 0 && !!selectedOption && (!needsCustomsChoice || customsRectified !== null);

  const handleConfirm = async () => {
    if (!order) return;
    let values: any;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    if (!selectedOption) {
      message.error('Selezionare la fase in cui si trova la differenza');
      return;
    }
    const payload: RectifyDetailPayload = {
      new_quantity: values.new_quantity,
      reason_code: values.reason_code,
      notes: values.notes?.trim() || undefined,
      stage: selectedOption.stage,
      shipment_id: selectedOption.shipment_id,
      customs_document_item_id: selectedOption.customs_document_item_id,
      customs_rectified: needsCustomsChoice ? customsRectified : null
    };
    setSubmitting(true);
    try {
      const result = await supplierAPI.rectifyDetailQuantity(order.id, values.detail_id, payload);
      message.success(result?.message || 'Quantità rettificata');
      onSuccess();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      message.error(typeof detail === 'string' ? detail : 'Errore durante la rettifica');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={`Rettifica Quantità — Ordine ${order?.order_number || ''}`}
      open={visible}
      onCancel={onCancel}
      width={860}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onCancel}>Annulla</Button>,
        <Button key="ok" type="primary" onClick={handleConfirm} loading={submitting} disabled={!canConfirm || loadingPreview}>
          Conferma rettifica
        </Button>
      ]}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="La quantità reale della partita è diversa da quella registrata"
        description="Indica la riga, la nuova quantità e il motivo. Il programma trova dove si trova la merce (produzione, transito o magazzino) e riallinea insieme riga d'ordine, riga di spedizione e bolletta. Se il fornitore manderà meno pezzi di quelli ordinati usa invece Annulla Residuo."
      />
      <Form form={form} layout="vertical" onValuesChange={handleFieldsChange}>
        <Form.Item name="detail_id" label="Riga d'ordine" rules={[{ required: true, message: 'Seleziona la riga' }]}>
          <Select placeholder="Seleziona il prodotto" showSearch optionFilterProp="children">
            {details.map((d) => (
              <Option key={d.id} value={d.id}>{detailLabel(d)}</Option>
            ))}
          </Select>
        </Form.Item>
        <Space size="large" align="start" wrap>
          <Form.Item name="new_quantity" label="Nuova quantità della riga" rules={[{ required: true, message: 'Indica la quantità' }]}>
            <InputNumber min={1} style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="reason_code" label="Motivo" rules={[{ required: true, message: 'Indica il motivo' }]} style={{ minWidth: 320 }}>
            <Select placeholder="Seleziona il motivo">
              {reasons.map((r) => <Option key={r.code} value={r.code}>{r.label}</Option>)}
            </Select>
          </Form.Item>
        </Space>
        <Form.Item name="notes" label="Note (opzionali)">
          <TextArea rows={2} className="no-uppercase-modal" />
        </Form.Item>
      </Form>

      <Spin spinning={loadingPreview}>
        <RectificationPreviewPanel
          preview={preview}
          loading={loadingPreview || submitting}
          selectedOptionKey={selectedOption ? optionKey(selectedOption) : undefined}
          onSelectOption={handleSelectOption}
          customsRectified={customsRectified}
          onCustomsRectifiedChange={handleCustomsRectified}
        />
      </Spin>
    </Modal>
  );
};

export default SupplierOrderRectifyModal;
